/**
 * Central state for the Report Builder editor.
 *
 * Owns the four mutable JSONB blobs (`report_config`, `dashboard_config`,
 * `ai_config`) plus the dirty bit
 * and the selection state used by the Dashboard tab. Every page-level
 * action (add section, edit KPI label, change tab, etc.) flows through
 * the methods exposed by this hook.
 *
 * Save is **explicit** — callers invoke `save()` from a button. Auto-save
 * lands in Fase 8.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { t } from "@/i18n";

import {
  addItem as addItemOp,
  addSection as addSectionOp,
  createItem,
  createSection as createSectionOp,
  emptyDashboard,
  moveSection as moveSectionOp,
  removeItem as removeItemOp,
  removeSection as removeSectionOp,
  setItemWidth as setItemWidthOp,
  updateItem as updateItemOp,
  updateSection as updateSectionOp,
} from "@/lib/dashboard-helpers";
import {
  draftsApi,
  validateApi,
  viewsConflictOf,
  type DraftUpdatePayload,
} from "@/lib/builder-api";
import { shortId } from "@/lib/utils";
import type {
  AIConfig,
  AiHomeViewsConflict,
  DashboardConfigV2,
  DashboardFilter,
  DashboardItem,
  ElementType,
  ReportConfig,
  ReportDraft,
  SectionConfig,
  ValidationResult,
} from "@/types";

// ── Selection (UI-only, never persisted) ────────────────

export interface Selection {
  sectionId: string | null;
  itemId: string | null;
}

const NO_SELECTION: Selection = { sectionId: null, itemId: null };

// ── State + reducer ───────────────────────────────────────

interface DraftState {
  report: ReportConfig;
  dashboard: DashboardConfigV2;
  ai: AIConfig;
  /** Increment whenever any of the configs changes. Used to mark dirty. */
  rev: number;
}

type Action =
  | { type: "hydrate"; draft: ReportDraft }
  | { type: "setReport"; patch: Partial<ReportConfig> }
  | { type: "replaceDashboard"; dashboard: DashboardConfigV2 }
  | { type: "setAI"; patch: Partial<AIConfig> };

function reducer(state: DraftState, action: Action): DraftState {
  switch (action.type) {
    case "hydrate":
      return {
        report: action.draft.report_config ?? {},
        dashboard:
          (action.draft.dashboard_config as DashboardConfigV2) ??
          emptyDashboard(),
        ai: action.draft.ai_config ?? {},
        rev: 0,
      };
    case "setReport":
      return { ...state, report: { ...state.report, ...action.patch }, rev: state.rev + 1 };
    case "replaceDashboard":
      return { ...state, dashboard: action.dashboard, rev: state.rev + 1 };
    case "setAI":
      return { ...state, ai: { ...state.ai, ...action.patch }, rev: state.rev + 1 };
  }
}

const INITIAL_STATE: DraftState = {
  report: {},
  dashboard: emptyDashboard(),
  ai: {},
  rev: 0,
};

// ── Hook ──────────────────────────────────────────────────

export type LoadingStatus = "loading" | "ready" | "error";

export interface UseBuilderDraft {
  // identity / lifecycle
  key: string;
  status: LoadingStatus;
  error: string | null;
  reload: () => Promise<void>;

  // server-tracked state
  draft: ReportDraft | null;
  dirty: boolean;
  saving: boolean;
  saveError: string | null;
  /** null = persisted (or nothing to save); a string = why it was NOT
   *  persisted. Never rejects. */
  save: () => Promise<string | null>;
  /** A save of the PUBLISHED report would break saved AI Home views (the
   *  server's 409): nothing was saved and auto-save waits until the person
   *  decides (2026-10-26). */
  viewsConflict: AiHomeViewsConflict | null;
  /** "Guardar igual": the same save, confirming the broken views. */
  saveConfirmingViews: () => Promise<string | null>;
  /** "Seguir editando": close the warning, keep the unsaved changes. */
  dismissViewsConflict: () => void;

  // publish / unpublish / validate
  publishing: boolean;
  /** ``confirmBrokenViews``: the person saw the saved AI Home views this
   *  publish breaks (the Publish modal lists them) and publishes anyway. */
  publish: (confirmBrokenViews?: boolean) => Promise<ValidationResult | null>;
  unpublish: () => Promise<void>;
  unpublishError: string | null;
  validate: () => Promise<ValidationResult>;
  isPublished: boolean;

  // auto-save toggle (persisted per session)
  autoSave: boolean;
  setAutoSave: (on: boolean) => void;

  // configs
  report: ReportConfig;
  dashboard: DashboardConfigV2;
  ai: AIConfig;

  // patchers
  setReport: (patch: Partial<ReportConfig>) => void;
  setAI: (patch: Partial<AIConfig>) => void;
  /**
   * Patch top-level dashboard fields (``runtime_view_sql``,
   * ``version``, etc.). Sections/items/filters have their own
   * ops above — use this only for fields that don't fit those.
   */
  setDashboard: (patch: Partial<DashboardConfigV2>) => void;

  // section-level dashboard ops
  addSection: (index?: number) => SectionConfig;
  removeSection: (sectionId: string) => void;
  updateSection: (sectionId: string, patch: Partial<SectionConfig>) => void;
  moveSection: (fromIndex: number, toIndex: number) => void;
  duplicateSection: (sectionId: string) => SectionConfig | null;

  // item-level dashboard ops
  addItem: (sectionId: string, element: ElementType) => DashboardItem;
  removeItem: (sectionId: string, itemId: string) => void;
  updateItem: (
    sectionId: string,
    itemId: string,
    patch: Partial<DashboardItem>,
  ) => void;
  setItemWidth: (sectionId: string, itemId: string, width: number) => void;
  moveItem: (sectionId: string, fromIndex: number, toIndex: number) => void;
  duplicateItem: (sectionId: string, itemId: string) => DashboardItem | null;

  // filter ops (apply to global_filters when sectionId is null)
  addFilter: (sectionId: string | null) => DashboardFilter;
  removeFilter: (sectionId: string | null, filterKey: string) => void;
  updateFilter: (
    sectionId: string | null,
    filterKey: string,
    patch: Partial<DashboardFilter>,
  ) => void;
  duplicateFilter: (
    sectionId: string | null,
    filterKey: string,
  ) => DashboardFilter | null;

  // selection (UI-only)
  selection: Selection;
  selectSection: (sectionId: string | null) => void;
  selectItem: (sectionId: string, itemId: string) => void;
  clearSelection: () => void;
}

const AUTO_SAVE_KEY = "builder.autoSave";

export interface PublishSteps {
  saving: boolean;
  dirty: boolean;
  save: () => Promise<string | null>;
  validate: () => Promise<ValidationResult>;
  publish: () => Promise<void>;
}

/** Save → validate → publish, stopping LOUDLY at the first step that
 *  did not happen. The server publishes the row it has stored, so
 *  publishing after an unpersisted save ships the previous version. */
export async function runPublishSequence(
  s: PublishSteps,
): Promise<ValidationResult> {
  if (s.saving) {
    return { valid: false, errors: [t("editor.saveInProgress")], warnings: [] };
  }
  if (s.dirty) {
    const saveError = await s.save();
    if (saveError !== null) {
      return { valid: false, errors: [saveError], warnings: [] };
    }
  }
  const result = await s.validate();
  if (!result.valid) return result;
  await s.publish();
  return { valid: true, errors: [], warnings: result.warnings };
}

export function useBuilderDraft(key: string): UseBuilderDraft {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [draft, setDraft] = useState<ReportDraft | null>(null);
  const [status, setStatus] = useState<LoadingStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [viewsConflict, setViewsConflict] = useState<AiHomeViewsConflict | null>(null);
  const [unpublishError, setUnpublishError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [selection, setSelection] = useState<Selection>(NO_SELECTION);
  const [autoSave, setAutoSaveState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(AUTO_SAVE_KEY) === "1";
  });

  /** Snapshot of `rev` at the last successful save. dirty := state.rev !== savedRev. */
  const [savedRev, setSavedRev] = useState(0);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    setStatus("loading");
    setError(null);
    try {
      const fresh = await draftsApi.get(key);
      if (!mounted.current) return;
      setDraft(fresh);
      dispatch({ type: "hydrate", draft: fresh });
      setSavedRev(0);
      setSelection(NO_SELECTION);
      setStatus("ready");
    } catch (err) {
      if (!mounted.current) return;
      setError(err instanceof Error ? err.message : "Failed to load draft");
      setStatus("error");
    }
  }, [key]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const dirty = state.rev !== savedRev;

  // ── Mutators ────────────────────────────────────────

  const setReport = useCallback((patch: Partial<ReportConfig>): void => {
    dispatch({ type: "setReport", patch });
  }, []);
  const setAI = useCallback((patch: Partial<AIConfig>): void => {
    dispatch({ type: "setAI", patch });
  }, []);
  const setDashboard = useCallback(
    (patch: Partial<DashboardConfigV2>): void => {
      // Merge top-level dashboard fields. The reducer's
      // ``replaceDashboard`` action wants a complete dashboard,
      // so we splat the current state with the patch on top.
      dispatch({
        type: "replaceDashboard",
        dashboard: { ...state.dashboard, ...patch },
      });
    },
    [state.dashboard],
  );

  // Section ops capture state via closure on every render — fine here
  // because they're invoked from event handlers, not effects.
  const addSection = useCallback(
    (index?: number): SectionConfig => {
      // U3 (2026-05-18 audit): default title is now ``"Section ${n}"``
      // / ``"Sección ${n}"`` based on the existing section count, in a
      // bilingual ``{en, es}`` shape so it renders correctly on both
      // locales without the editor user having to fill both inputs.
      // Numbering uses ``sections.length + 1`` so the first ad-hoc
      // section is "Section 1" / "Sección 1" — feels natural to the
      // user, even when ``index`` inserts before existing ones (we
      // don't try to renumber; the user is free to rename).
      const nextNumber = state.dashboard.sections.length + 1;
      const next = addSectionOp(
        state.dashboard,
        index ?? state.dashboard.sections.length,
        createSectionOp({
          title: { en: `Section ${nextNumber}`, es: `Sección ${nextNumber}` },
        }),
      );
      const created = next.sections[index ?? next.sections.length - 1]!;
      dispatch({ type: "replaceDashboard", dashboard: next });
      setSelection({ sectionId: created.id, itemId: null });
      return created;
    },
    [state.dashboard],
  );

  const removeSection = useCallback(
    (sectionId: string): void => {
      dispatch({
        type: "replaceDashboard",
        dashboard: removeSectionOp(state.dashboard, sectionId),
      });
      setSelection((sel) =>
        sel.sectionId === sectionId ? NO_SELECTION : sel,
      );
    },
    [state.dashboard],
  );

  const updateSection = useCallback(
    (sectionId: string, patch: Partial<SectionConfig>): void => {
      dispatch({
        type: "replaceDashboard",
        dashboard: updateSectionOp(state.dashboard, sectionId, patch),
      });
    },
    [state.dashboard],
  );

  const addItem = useCallback(
    (sectionId: string, element: ElementType): DashboardItem => {
      const item = createItem(element);
      dispatch({
        type: "replaceDashboard",
        dashboard: addItemOp(state.dashboard, sectionId, item),
      });
      setSelection({ sectionId, itemId: item.id });
      return item;
    },
    [state.dashboard],
  );

  const removeItem = useCallback(
    (sectionId: string, itemId: string): void => {
      dispatch({
        type: "replaceDashboard",
        dashboard: removeItemOp(state.dashboard, sectionId, itemId),
      });
      setSelection((sel) =>
        sel.itemId === itemId ? { sectionId: sel.sectionId, itemId: null } : sel,
      );
    },
    [state.dashboard],
  );

  const updateItem = useCallback(
    (sectionId: string, itemId: string, patch: Partial<DashboardItem>): void => {
      dispatch({
        type: "replaceDashboard",
        dashboard: updateItemOp(state.dashboard, sectionId, itemId, patch),
      });
    },
    [state.dashboard],
  );

  const setItemWidth = useCallback(
    (sectionId: string, itemId: string, width: number): void => {
      dispatch({
        type: "replaceDashboard",
        dashboard: setItemWidthOp(state.dashboard, sectionId, itemId, width),
      });
    },
    [state.dashboard],
  );

  const moveSection = useCallback(
    (fromIndex: number, toIndex: number): void => {
      dispatch({
        type: "replaceDashboard",
        dashboard: moveSectionOp(state.dashboard, fromIndex, toIndex),
      });
    },
    [state.dashboard],
  );

  const duplicateSection = useCallback(
    (sectionId: string): SectionConfig | null => {
      const original = state.dashboard.sections.find((s) => s.id === sectionId);
      if (!original) return null;
      // Deep-clone items with fresh ids so the copy edits independently.
      const clone: SectionConfig = {
        ...original,
        id: shortId("sec"),
        title: original.title
          ? `${original.title} (copy)`
          : "Copied section",
        filters: JSON.parse(JSON.stringify(original.filters ?? [])),
        items: (original.items ?? []).map((item) => ({
          ...item,
          id: shortId(item.element),
          props: JSON.parse(JSON.stringify(item.props ?? {})),
        })),
        config: JSON.parse(JSON.stringify(original.config ?? {})),
      };
      const fromIdx = state.dashboard.sections.findIndex((s) => s.id === sectionId);
      const sections = [...state.dashboard.sections];
      sections.splice(fromIdx + 1, 0, clone);
      dispatch({
        type: "replaceDashboard",
        dashboard: { ...state.dashboard, sections },
      });
      setSelection({ sectionId: clone.id, itemId: null });
      return clone;
    },
    [state.dashboard],
  );

  const moveItem = useCallback(
    (sectionId: string, fromIndex: number, toIndex: number): void => {
      const section = state.dashboard.sections.find((s) => s.id === sectionId);
      if (!section) return;
      if (fromIndex === toIndex) return;
      const items = [...section.items];
      const [moved] = items.splice(fromIndex, 1);
      if (!moved) return;
      items.splice(Math.max(0, Math.min(toIndex, items.length)), 0, moved);
      dispatch({
        type: "replaceDashboard",
        dashboard: updateSectionOp(state.dashboard, sectionId, { items }),
      });
    },
    [state.dashboard],
  );

  const duplicateItem = useCallback(
    (sectionId: string, itemId: string): DashboardItem | null => {
      const section = state.dashboard.sections.find((s) => s.id === sectionId);
      const original = section?.items.find((i) => i.id === itemId);
      if (!section || !original) return null;
      const clone: DashboardItem = {
        ...original,
        id: shortId(original.element),
        // Deep-clone props so the copy edits independently.
        props: JSON.parse(JSON.stringify(original.props ?? {})),
      };
      const fromIdx = section.items.findIndex((i) => i.id === itemId);
      const items = [...section.items];
      items.splice(fromIdx + 1, 0, clone);
      dispatch({
        type: "replaceDashboard",
        dashboard: updateSectionOp(state.dashboard, sectionId, { items }),
      });
      setSelection({ sectionId, itemId: clone.id });
      return clone;
    },
    [state.dashboard],
  );

  // ── Filter ops (sectionId === null → global_filters) ──

  const addFilter = useCallback(
    (sectionId: string | null): DashboardFilter => {
      const filter: DashboardFilter = {
        key: shortId("flt"),
        column: "",
        type: "select",
        required: false,
      };
      if (sectionId === null) {
        dispatch({
          type: "replaceDashboard",
          dashboard: {
            ...state.dashboard,
            global_filters: [...state.dashboard.global_filters, filter],
          },
        });
      } else {
        dispatch({
          type: "replaceDashboard",
          dashboard: updateSectionOp(state.dashboard, sectionId, {
            filters: [
              ...(state.dashboard.sections.find((s) => s.id === sectionId)
                ?.filters ?? []),
              filter,
            ],
          }),
        });
      }
      return filter;
    },
    [state.dashboard],
  );

  const removeFilter = useCallback(
    (sectionId: string | null, filterKey: string): void => {
      if (sectionId === null) {
        dispatch({
          type: "replaceDashboard",
          dashboard: {
            ...state.dashboard,
            global_filters: state.dashboard.global_filters.filter(
              (f) => f.key !== filterKey,
            ),
          },
        });
      } else {
        const section = state.dashboard.sections.find((s) => s.id === sectionId);
        if (!section) return;
        dispatch({
          type: "replaceDashboard",
          dashboard: updateSectionOp(state.dashboard, sectionId, {
            filters: section.filters.filter((f) => f.key !== filterKey),
          }),
        });
      }
    },
    [state.dashboard],
  );

  const updateFilter = useCallback(
    (
      sectionId: string | null,
      filterKey: string,
      patch: Partial<DashboardFilter>,
    ): void => {
      if (sectionId === null) {
        dispatch({
          type: "replaceDashboard",
          dashboard: {
            ...state.dashboard,
            global_filters: state.dashboard.global_filters.map((f) =>
              f.key === filterKey ? { ...f, ...patch } : f,
            ),
          },
        });
      } else {
        const section = state.dashboard.sections.find((s) => s.id === sectionId);
        if (!section) return;
        dispatch({
          type: "replaceDashboard",
          dashboard: updateSectionOp(state.dashboard, sectionId, {
            filters: section.filters.map((f) =>
              f.key === filterKey ? { ...f, ...patch } : f,
            ),
          }),
        });
      }
    },
    [state.dashboard],
  );

  const duplicateFilter = useCallback(
    (
      sectionId: string | null,
      filterKey: string,
    ): DashboardFilter | null => {
      const list =
        sectionId === null
          ? state.dashboard.global_filters
          : state.dashboard.sections.find((s) => s.id === sectionId)?.filters ?? [];
      const original = list.find((f) => f.key === filterKey);
      if (!original) return null;
      // Pick a new unique key — "<key>_copy" or "<key>_copy_N".
      const existingKeys = new Set(list.map((f) => f.key));
      let candidate = `${original.key}_copy`;
      let n = 2;
      while (existingKeys.has(candidate)) {
        candidate = `${original.key}_copy_${n}`;
        n += 1;
      }
      const clone: DashboardFilter = { ...original, key: candidate };
      const fromIdx = list.findIndex((f) => f.key === filterKey);
      const next = [...list];
      next.splice(fromIdx + 1, 0, clone);

      if (sectionId === null) {
        dispatch({
          type: "replaceDashboard",
          dashboard: { ...state.dashboard, global_filters: next },
        });
      } else {
        dispatch({
          type: "replaceDashboard",
          dashboard: updateSectionOp(state.dashboard, sectionId, { filters: next }),
        });
      }
      return clone;
    },
    [state.dashboard],
  );

  // ── Selection helpers ──────────────────────────────

  const selectSection = useCallback((sectionId: string | null): void => {
    setSelection(
      sectionId === null
        ? NO_SELECTION
        : { sectionId, itemId: null },
    );
  }, []);
  const selectItem = useCallback((sectionId: string, itemId: string): void => {
    setSelection({ sectionId, itemId });
  }, []);
  const clearSelection = useCallback(() => {
    setSelection(NO_SELECTION);
  }, []);

  // ── Save ───────────────────────────────────────────

  const persist = useCallback(async (confirmBrokenViews: boolean): Promise<string | null> => {
    if (!dirty) return null;
    if (saving) return t("editor.saveInProgress");
    setSaving(true);
    setSaveError(null);
    const revAtSubmit = state.rev;
    const payload: DraftUpdatePayload = {
      report_config: state.report,
      dashboard_config: state.dashboard,
      ai_config: state.ai,
      ...(confirmBrokenViews ? { confirm_broken_views: true } : {}),
    };
    try {
      const updated = await draftsApi.update(key, payload);
      if (mounted.current) {
        setDraft(updated);
        setSavedRev(revAtSubmit);
        setViewsConflict(null);
      }
      return null;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Save failed";
      if (mounted.current) {
        setSaveError(message);
        // Saved views this live save would break: the page asks first.
        const conflict = viewsConflictOf(err);
        if (conflict !== null) setViewsConflict(conflict);
      }
      return message; // not persisted, mounted or not
    } finally {
      if (mounted.current) setSaving(false);
    }
  }, [
    dirty,
    saving,
    key,
    state.rev,
    state.report,
    state.dashboard,
    state.ai,
  ]);
  const save = useCallback(() => persist(false), [persist]);
  const saveConfirmingViews = useCallback(() => persist(true), [persist]);
  const dismissViewsConflict = useCallback(() => setViewsConflict(null), []);

  // ── Validate / publish / unpublish ────────────────

  // The slug is the one the server resolved for this draft (every
  // single-draft route returns it); the editor never derives it.
  // null means the server found no project for the draft, and the
  // backend refuses it loudly — not a default, just the absence sent.
  const projectSlug = draft?.project_slug ?? null;
  const validate = useCallback(async (): Promise<ValidationResult> => {
    return validateApi.run(state.report, state.dashboard, projectSlug);
  }, [state.report, state.dashboard, projectSlug]);

  const publish = useCallback(async (confirmBrokenViews = false): Promise<ValidationResult | null> => {
    if (publishing) return null;
    setPublishing(true);
    try {
      return await runPublishSequence({
        saving,
        dirty,
        save,
        validate,
        publish: async () => {
          const updated = await draftsApi.publish(key, confirmBrokenViews);
          if (mounted.current) setDraft(updated);
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Publish failed";
      return { valid: false, errors: [message], warnings: [] };
    } finally {
      if (mounted.current) setPublishing(false);
    }
  }, [publishing, saving, dirty, key, save, validate]);

  const unpublish = useCallback(async (): Promise<void> => {
    if (publishing) return;
    setPublishing(true);
    setUnpublishError(null);
    try {
      const updated = await draftsApi.unpublish(key);
      if (mounted.current) setDraft(updated);
    } catch (err) {
      if (mounted.current) {
        setUnpublishError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (mounted.current) setPublishing(false);
    }
  }, [publishing, key]);

  // ── Auto-save (debounced 1.5s after rev changes) ──

  const setAutoSave = useCallback((on: boolean): void => {
    setAutoSaveState(on);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(AUTO_SAVE_KEY, on ? "1" : "0");
    }
  }, []);

  useEffect(() => {
    if (!autoSave) return;
    if (!dirty) return;
    if (saving) return;
    // A live save waiting on the person's answer about broken views.
    if (viewsConflict !== null) return;
    const handle = window.setTimeout(() => {
      void save();
    }, 1500);
    return () => window.clearTimeout(handle);
  }, [autoSave, dirty, saving, save, viewsConflict]);

  // ── Public surface ─────────────────────────────────

  return useMemo<UseBuilderDraft>(
    () => ({
      key,
      status,
      error,
      reload,

      draft,
      dirty,
      saving,
      saveError,
      save,
      viewsConflict,
      saveConfirmingViews,
      dismissViewsConflict,

      publishing,
      publish,
      unpublish,
      unpublishError,
      validate,
      isPublished: draft?.status === "published",

      autoSave,
      setAutoSave,

      report: state.report,
      dashboard: state.dashboard,
      ai: state.ai,

      setReport,
      setAI,
      setDashboard,

      addSection,
      removeSection,
      updateSection,
      moveSection,
      duplicateSection,

      addItem,
      removeItem,
      updateItem,
      setItemWidth,
      moveItem,
      duplicateItem,

      addFilter,
      removeFilter,
      updateFilter,
      duplicateFilter,

      selection,
      selectSection,
      selectItem,
      clearSelection,
    }),
    [
      key,
      status,
      error,
      reload,
      draft,
      dirty,
      saving,
      saveError,
      save,
      viewsConflict,
      saveConfirmingViews,
      dismissViewsConflict,
      publishing,
      publish,
      unpublish,
      unpublishError,
      validate,
      autoSave,
      setAutoSave,
      state.report,
      state.dashboard,
      state.ai,
      setReport,
      setAI,
      setDashboard,
      addSection,
      removeSection,
      updateSection,
      moveSection,
      duplicateSection,
      addItem,
      removeItem,
      updateItem,
      setItemWidth,
      moveItem,
      duplicateItem,
      addFilter,
      removeFilter,
      updateFilter,
      duplicateFilter,
      selection,
      selectSection,
      selectItem,
      clearSelection,
    ],
  );
}
